import { useInAppNotification } from '@src/context/InAppNotificationContext';
import ThreadPanelDesktop from '@src/features/shared/community/components/thread/ThreadPanelDesktop';
import ThreadPanelMobile from '@src/features/shared/community/components/thread/ThreadPanelMobile';
import { THREAD_POLLING_INTERVAL } from '@src/features/shared/community/constants/polling';
import { useThread } from '@src/features/shared/community/context/ThreadContext';
import { useIsMobile } from '@src/hooks/useIsMobile';
import { AppDispatch, RootState } from '@src/store/store';
import { fetchThreads } from '@src/store/threadSlice';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

interface ThreadPanelProps {
  width: number;
  onWidthChange: (width: number) => void;
  minWidth?: number;
  maxWidth?: number;
}

const ThreadPanel: React.FC<ThreadPanelProps> = ({ width, onWidthChange, minWidth, maxWidth }) => {
  const isMobile = useIsMobile();
  const {
    isThreadOpen,
    parentChat,
    channel,
    activeThreadParentId,
    setParentChat,
    setThreadReplies,
    threadReplies,
  } = useThread();
  const dispatch = useDispatch<AppDispatch>();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const reduxThreadData = useSelector((state: RootState) => state.thread);
  const repliesRef = useRef(threadReplies);
  const { refetchNotifications } = useInAppNotification();

  useEffect(() => {
    repliesRef.current = threadReplies;
  }, [threadReplies]);

  // Reduxからのデータをコンテキストに同期
  useEffect(() => {
    if (reduxThreadData.parentChat && setParentChat) {
      setParentChat(reduxThreadData.parentChat);
      refetchNotifications();
    }
    if (reduxThreadData.threadReplies && setThreadReplies) {
      setThreadReplies(reduxThreadData.threadReplies);
    }
  }, [
    reduxThreadData.parentChat,
    reduxThreadData.threadReplies,
    setParentChat,
    setThreadReplies,
    refetchNotifications,
  ]);

  // スレッドデータのポーリング
  useEffect(() => {
    if (!isThreadOpen || !activeThreadParentId || !channel?.code) {
      // ポーリングを停止
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    // ポーリング開始
    pollingIntervalRef.current = setInterval(() => {
      dispatch(
        fetchThreads({
          channel,
          parentChatId: activeThreadParentId,
          markAsRead: true,
        }),
      );
    }, THREAD_POLLING_INTERVAL);

    // クリーンアップ
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [isThreadOpen, activeThreadParentId, channel, dispatch]);

  if (!isThreadOpen) {
    return null;
  }

  if (isMobile) {
    return <ThreadPanelMobile parentChat={parentChat} />;
  }

  return (
    <ThreadPanelDesktop
      parentChat={parentChat}
      width={width}
      onWidthChange={onWidthChange}
      minWidth={minWidth}
      maxWidth={maxWidth}
    />
  );
};

export default ThreadPanel;
